import React, { useCallback, useMemo } from 'react';
import { DateRangePicker } from 'react-date-range';
import ReactDatePickerNew from 'react-datepicker-new';
import ReactDatePickerOld from 'react-datepicker-old';
import onClickOutside from 'react-onclickoutside';
import cn from 'classnames';
import { enGB } from 'date-fns/locale';
import moment from 'moment';

import { SlickError } from 'errors/Slick.error';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToDate, fromAnyDateToIso } from 'utils/converters/time/from.util';
import { nowAsDate } from 'utils/date/now.util';
import { SCHEDULER_WEEK_LOCALE } from 'utils/date/schedulerWeekBound.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import logger from 'utils/logger.util';
import parseDateOrRange from 'utils/parsers/parseDateOrRange.util';
import parseDateWeek from 'utils/parsers/parseDateWeek.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isFn from 'utils/predicates/function/isFn.util';
import isValidDateObject from 'utils/predicates/time/isValidDateObject.util';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'react-datepicker-old/dist/react-datepicker.css';
// import 'react-datepicker-new/dist/react-datepicker.css'; // or import 'react-datepicker-new/dist/react-datepicker-cssmodules.css';
import 'react-datepicker-new/dist/react-datepicker-cssmodules.css';
import CN from './DatePickerInput.module.scss';

const L = logger('DatePickerInput');

const USE_PARSED_INTERVAL = true;

// SEE: http://github.com/hypeserver/react-date-range
// SEE: https://reactdatepicker.com/
// SEE: https://github.com/Hacker0x01/react-datepicker
// SEE: https://github.com/Hacker0x01/react-datepicker/blob/main/docs/datepicker.md

// TODO: remove the unmaintained react-date-range library and replace the picker with another

const generateDefaultRanges = () => [
  {
    startDate: nowAsDate(),
    endDate: nowAsDate(),
    key: 'selection',
  },
];

const filterRanges = (ranges) => {
  const FTAG = 'filterRanges()';

  // We allow the ranges to be undefined, that's fine
  if (isMissing(ranges)) return generateDefaultRanges();

  // but for other cases, we notify with a warning that the ranges are invalid, and still use the default

  if (!Array.isArray(ranges) || !ranges.length) {
    L.warn(FTAG, 'Not full ranges:', ranges);
    return generateDefaultRanges();
  }

  const filtered = [...ranges]
    .map((range) => {
      const isBgnValid = isValidDateObject(range?.startDate);
      const isEndValid = isValidDateObject(range?.endDate);

      if (!isBgnValid || !isEndValid) L.info(FTAG, 'Invalid range:', range, { isBgnValid, isEndValid });

      if (isBgnValid && isEndValid) return L.info(FTAG, 'returning:', range);
      if (isBgnValid) return L.info(FTAG, 'returning:', { ...range, endDate: range?.startDate });
      if (isEndValid) return L.info(FTAG, 'returning:', { ...range, startDate: range?.endDate });

      return L.info(FTAG, 'returning:', null);
    })
    .filter(alwaysSame);

  return filtered.length ? filtered : generateDefaultRanges();
};

// DEPRECATED: please use  'react-datepicker' or something newer than this unmaintained DateRangePicker
class UndismissableDateRangePicker extends DateRangePicker {
  // noinspection JSUnusedGlobalSymbols
  handleClickOutside = alwaysNoop;
}

class DateRangePickerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
  }

  render() {
    const children = this.props.children;
    const { error, errorInfo } = this.state;

    if (!errorInfo) return children;

    const element = document.querySelector('.close-overlay');
    L.warn('DateRangePickerErrorBoundary.render()', { element, error, errorInfo });

    element?.click?.();

    return <div></div>;
  }
}

const WeekRangePicker = (props) => (
  <DateRangePickerErrorBoundary>
    <UndismissableDateRangePicker
      {...props}
      ranges={props.prefiltered ? props.ranges : filterRanges(props.ranges)}
      locale={enGB}
      showPreview={false}
      direction="horizontal"
      moveRangeOnFirstSelection={false}
    />
  </DateRangePickerErrorBoundary>
);

const PrefilteredWeekPicker = ({ date, bgn, end, ...extra }) => {
  const { is, endZulu, bgnZulu } = parseDateWeek(date);

  // SEE: https://getslick.atlassian.net/browse/GS-13377 (Date Picker Bug: Calendar, Week View)
  const isParsedValid = USE_PARSED_INTERVAL && is.valid;

  const bgnIso = fromAnyDateToIso(isParsedValid ? bgnZulu : bgn);
  const endIso = fromAnyDateToIso(isParsedValid ? endZulu : end);

  const ranges = useMemo(
    () => [
      {
        startDate: fromAnyDateToDate(bgnIso),
        endDate: fromAnyDateToDate(endIso),
        key: 'selection',
      },
    ],
    [bgnIso, endIso],
  );

  return <WeekRangePicker {...extra} prefiltered ranges={ranges} />;
};

class WeeksComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      weeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      currentWeek: moment(this.props.selectedDate).locale(SCHEDULER_WEEK_LOCALE).week(),
    };
  }

  onJumpWeeks = (weeks) => {
    const { onChangeDate, selectedDate } = this.props;
    onChangeDate(moment(selectedDate).add(weeks, 'week'));
  };

  render() {
    const { weeks, currentWeek } = this.state;
    const { t } = this.props;

    return (
      <div className="weeks-container cfix">
        <span>{this.props.t('composite.dateRangePicker.skipForward')}</span>
        <div className="weeks">
          {weeks.map((week) => (
            <div role="button" tabIndex={0} onClick={() => void this.onJumpWeeks(week)} className={week === currentWeek ? 'w-item active' : 'w-item'} key={week}>
              {`${String(week)}${t('composite.dateRangePicker.weekShort')}`}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

class DatePickerInputErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
  }

  render() {
    const children = this.props.children;
    const tag = fromAnyToTrimmed(this.props.tag);
    const version = fromAnyToTrimmed(this.props.v);
    const { error, errorInfo } = this.state;

    if (!errorInfo) return children;

    const element = document.querySelector('.close-overlay');
    L.warn('DateRangePickerErrorBoundary.render()', { element, error, errorInfo });

    element?.click?.();

    return <div data-bem="DatePickerInputErrorBoundary" data-tag={tag} data-version={version}></div>;
  }
}

const CommonDatePicker = onClickOutside(
  class CommonDatePickerInternal extends ReactDatePickerOld {
    // noinspection JSUnusedGlobalSymbols
    handleClickOutside = () => void (this.props.isShowing && this.props.onOutsideClick());
  },
);

const VersionedDatePicker = (props) => {
  const { isDarkMode } = useTheme();
  const tag = fromAnyToTrimmed(props.tag);
  const version = fromAnyToTrimmed(props.v);

  const onChange = isFn(props?.onChange) ? props.onChange : alwaysNoop;
  const onSelect = isFn(props?.onSelect) ? props.onSelect : alwaysNoop;

  const handleChange = useCallback((ev) => void call('VersionedDatePicker.onChange', onChange, parseDateOrRange(ev)), [onChange]);
  const handleSelect = useCallback((ev) => void call('VersionedDatePicker.onSelect', onSelect, parseDateOrRange(ev)), [onSelect]);

  L.debug('VersionedDatePicker()', props);

  return (
    <div
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <DatePickerInputErrorBoundary tag={tag} version={version}>
        <ReactDatePickerNew
          {...props}
          onChange={handleChange}
          onSelect={handleSelect}
          disabledKeyboardNavigation // https://stackoverflow.com/questions/62417394/react-datepicker-why-is-same-day-of-month-highlightedhttps://stackoverflow.com/questions/62417394/react-datepicker-why-is-same-day-of-month-highlighted
          locale={enGB}
          calendarStartDay={1}
          calendarClassName={cn({
            [CN.versionedDatePicker]: true,
            [`VersionedDatePicker__version--${version}`]: version,
            [`VersionedDatePicker__tag--${tag}`]: tag,
          })}
        />
      </DatePickerInputErrorBoundary>
    </div>
  );
};

// DEPRECATED: Try using some of the Calendar*Input components
const DatePickerInput = ({ tag: tagFromProps, v: vFromProps, component, ...extra }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const tag = fromAnyToTrimmed(tagFromProps);
  const v = fromAnyToTrimmed(vFromProps);

  if ('3' === v) return <VersionedDatePicker {...extra} v={v} isDarkMode={theme.isDarkMode} />;
  if (v) throw new SlickError({ message: `Bad prop v for ${tag}, it must be "3" or not used at all` });

  const next = { ...extra, ...theme, calendarStartDay: 1, weekStartsOn: 1, t };

  if (component === DatePickerInput.ReactDatePickerOld) return <DatePickerInput.ReactDatePickerOld {...next} />;
  if (component === DatePickerInput.WeekRangePicker) return <DatePickerInput.WeekRangePicker {...next} />;
  if (component === DatePickerInput.CommonDatePicker) return <DatePickerInput.CommonDatePicker {...next} />;
  if (component === DatePickerInput.WeeksComponent) return <DatePickerInput.WeeksComponent {...next} />;
  if (component === DatePickerInput.PrefilteredWeekPicker) return <DatePickerInput.PrefilteredWeekPicker {...next} />;

  const message = `Missing or invalid component in DatePickerInput for ${tag}`;
  L.warn('()', message, component);
  throw new SlickError({ message });
};

DatePickerInput.ReactDatePickerOld = ReactDatePickerOld;
DatePickerInput.WeekRangePicker = WeekRangePicker;
DatePickerInput.PrefilteredWeekPicker = PrefilteredWeekPicker;
DatePickerInput.CommonDatePicker = CommonDatePicker;
DatePickerInput.WeeksComponent = WeeksComponent;

export default DatePickerInput;
